import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';

const DEFAULT_MAINTENANCE_URL = 'https://cdn.cloubot.com/common/maintenance.json';
const DEFAULT_POLL_MS = 60000;
const DEFAULT_TIMEOUT_MS = 8000;

const toNumberOrDefault = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isMaintenanceActive = (data, now) => {
  if (!data?.enabled) return false;

  const startAt = parseDate(data.startAt);
  const endAt = parseDate(data.endAt);
  const currentTime = now.getTime();

  if (startAt && currentTime < startAt.getTime()) return false;
  if (endAt && currentTime > endAt.getTime()) return false;

  return true;
};

const formatDateTime = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return null;

  return parsed.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizePayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  return {
    enabled: raw.enabled === true,
    title: typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : 'Service Notice',
    message:
      typeof raw.message === 'string' && raw.message.trim()
        ? raw.message.trim()
        : 'Service is temporarily unavailable. Please try again shortly.',
    startAt: typeof raw.startAt === 'string' ? raw.startAt : null,
    endAt: typeof raw.endAt === 'string' ? raw.endAt : null,
    blocking: raw.blocking !== false,
  };
};

const MaintenanceNotice = () => {
  const [payload, setPayload] = useState(null);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);

  const maintenanceUrl = process.env.REACT_APP_MAINTENANCE_JSON_URL || DEFAULT_MAINTENANCE_URL;
  const pollMs = toNumberOrDefault(process.env.REACT_APP_MAINTENANCE_POLL_MS, DEFAULT_POLL_MS);
  const timeoutMs = toNumberOrDefault(process.env.REACT_APP_MAINTENANCE_TIMEOUT_MS, DEFAULT_TIMEOUT_MS);

  useEffect(() => {
    isMountedRef.current = true;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const checkMaintenance = async () => {
      if (!isMountedRef.current) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const separator = maintenanceUrl.includes('?') ? '&' : '?';
      const requestUrl = `${maintenanceUrl}${separator}t=${Date.now()}`;

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load maintenance.json: ${response.status}`);
        }

        const json = await response.json();
        const normalized = normalizePayload(json);

        if (!isMountedRef.current || !normalized) return;

        const active = isMaintenanceActive(normalized, new Date());

        setPayload(normalized);
        setVisible(active);

        if (active && !intervalRef.current) {
          intervalRef.current = setInterval(checkMaintenance, pollMs);
        }

        if (!active) {
          stopPolling();
        }
      } catch (error) {
        if (!isMountedRef.current) return;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkMaintenance();

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [maintenanceUrl, pollMs, timeoutMs]);

  const scheduleLabel = useMemo(() => {
    if (!payload?.startAt && !payload?.endAt) return null;

    const startLabel = formatDateTime(payload?.startAt);
    const endLabel = formatDateTime(payload?.endAt);

    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
    if (startLabel) return `Start: ${startLabel}`;
    if (endLabel) return `End: ${endLabel}`;
    return null;
  }, [payload]);

  const handleClose = () => {
    if (payload?.blocking) return;
    setVisible(false);
  };

  return (
    <Modal
      title={payload?.title || 'Service Notice'}
      visible={visible}
      onCancel={handleClose}
      onOk={handleClose}
      okText="Close"
      maskClosable={!payload?.blocking}
      closable={!payload?.blocking}
      keyboard={!payload?.blocking}
      footer={payload?.blocking ? null : undefined}
      width={520}
      zIndex={3000}
    >
      <p style={{ marginBottom: scheduleLabel ? 8 : 0 }}>{payload?.message}</p>
      {scheduleLabel ? <p style={{ marginBottom: 0, color: '#666' }}>{scheduleLabel}</p> : null}
    </Modal>
  );
};

export default MaintenanceNotice;
